import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { fetchPartners, seedPartners } from './partners';
import { applyPartnerVars } from './vars';
import { handleMsRedirect, getMsAccounts, getStoredGoogleToken } from './msal';
import { loadAllTokens, getGoogleTokenForPartner } from './supabaseAuth';
import { fetchActiveUsers, resolveDefaultSender } from './userSignatures';

import { fetchAllRows } from './fetchAll';
import { withContactOwner, contactOwnerForApp, configureContactScopes, setActiveContactScope } from './contactDataAdapter';
import { CONTACT_SCOPE_BY_PARTNER, CONTACT_CHILD_TABLES } from '../config/contactAppScopes';
import { requestAppScope } from './appScopeClaim';

configureContactScopes(CONTACT_SCOPE_BY_PARTNER, CONTACT_CHILD_TABLES);
const AppContext = createContext(null);

const WELL_REPD_PARTNER_ID = '6ad8ac03-049f-462f-83fc-8b2bbc7c5bdd';

export function AppProvider({ children }) {
  const [partners, setPartners] = useState([]);
  const [activePartner, setActivePartnerState] = useState(null);
  setActiveContactScope(activePartner); // helpers without component context read as the app that is open (set during render so child effects see it)
  const actingScope = contactOwnerForApp(activePartner);
  useEffect(() => { requestAppScope(supabase, actingScope); }, [actingScope]); // keeps the database app_scope claim in step with the open app

  // v1.7.39.147 — real distinction Shane drew: the Well Repd/partner
  // dashboards and a person's own personal dashboard are two different
  // things, reached two different ways. viewMode is the switch between
  // them -- 'partner' (default, existing Master/Persona split keyed off
  // activePartner) vs 'personal' (new, per-logged-in-user view, "Welcome
  // Shane"). Picking any real partner always resets to 'partner' mode,
  // whichever of the two switcher UIs (bottom-left sidebar list, or the
  // top-right HeaderPersonaChip) triggered it -- so choosing a partner
  // never leaves you stuck looking like you're still on the personal
  // view.
  const [viewMode, setViewMode] = useState(() => {
    return 'partner'; // Shane 2026-10-27: Charlie always opens on Well Repd; the last-used view is never restored
  });
  const setViewModeAndPersist = useCallback((mode) => {
    setViewMode(mode);
    try { localStorage.setItem('charlie_view_mode', mode); } catch (e) { /* ignore */ }
  }, []);

  // v1.7.39.207 -- real, Shane's ask 2026-08-04: a hidden admin-only
  // "switch user" preview, hardcoded to Shane's real user id, no
  // password gate (he's Master Admin). Lets him preview any other real
  // user's PersonalDashboard without actually re-authenticating as
  // them. Deliberately NOT persisted to localStorage -- always resets
  // to viewing your own dashboard on a fresh page load, so a forgotten
  // preview session never silently sticks around.
  const [viewAsUserId, setViewAsUserId] = useState(null);

  const setActivePartner = useCallback((partner) => {
    setActivePartnerState(partner);
    applyPartnerVars(partner);
    if (partner?.id) {
      localStorage.setItem('charlie_active_partner_id', partner.id);
    }
  }, []);

  // GenServ shell (#2503 / GS-0037): enter a partner for this session only. Not written to localStorage, so a reload or a new session comes back in the partner the user
  // chose themselves, and Lead Management / Hello never default their send-from to GenServ because of a link that was opened once.
  const setSessionPartner = useCallback((partner) => {
    setActivePartnerState(partner);
    applyPartnerVars(partner);
  }, []);

  // #1451 -- real, subtle persona-switch glow signal. A counter, not a
  // boolean: incrementing (rather than toggling true) means two switches
  // in quick succession are still two distinct signals PersonaSwitchGlow
  // can react to, even if the first glow hadn't finished fading yet.
  // Deliberately bumped ONLY here, inside chooseActivePartner -- never in
  // setActivePartner -- for the exact same real reason chooseActivePartner
  // exists as a separate function in the first place (see v1.7.39.147 note
  // above): boot-time session restore must never trigger a switch signal,
  // only an explicit, user-initiated partner pick should.
  const [personaSwitchSignal, setPersonaSwitchSignal] = useState(0);

  // v1.7.39.147 — deliberately separate from setActivePartner above.
  // setActivePartner also runs on initial boot (restoring the last
  // session's partner from localStorage) -- resetting viewMode there
  // unconditionally would silently kick someone off the personal
  // dashboard on every single page reload. This version is only ever
  // called from an explicit, user-initiated partner pick (both
  // switcher UIs), so it's safe to force viewMode back to 'partner'.
  const chooseActivePartner = useCallback((partner) => {
    setActivePartner(partner);
    setViewModeAndPersist('partner');
    setPersonaSwitchSignal((n) => n + 1);
  }, [setActivePartner, setViewModeAndPersist]);

  const [contacts, setContacts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tasks, setTasks] = useState([]);
  // v1.6.50 — templates hydrate from localStorage on cold boot so the picker
  // is never a blank slate while the network fetch is in flight or failing.
  const [templates, setTemplates] = useState(() => {
    try {
      const raw = localStorage.getItem('charlie_templates_cache_v1');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [sequences, setSequences] = useState([]);
  const [msAccounts, setMsAccounts] = useState([]);
  // Alyssa Edits v1: roster of active users + the default sender (Shane).
  const [users, setUsers] = useState([]);
  const [defaultSenderId, setDefaultSenderId] = useState(null);

  // Supabase-persisted token map: { [partnerId]: { microsoft: tokenRecord, google: tokenRecord } }
  const [partnerTokens, setPartnerTokens] = useState({});

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // A confirmation lasts 2.5 seconds (an error 5), tap to dismiss at once; a newer toast replaces the older one and its timer, so they never stack.
  const toastTimer = useRef(null);
  const dismissToast = useCallback(() => { if (toastTimer.current) clearTimeout(toastTimer.current); setToast(null); }, []);
  const showToast = useCallback((message, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 5000 : 2500);
  }, []);

  const loadPartners = useCallback(async () => {
    await seedPartners();
    // v1.7.40.89 -- real: load ALL partners including inactive, then let
    // consumers decide. PartnersManager genuinely needs to SEE inactive
    // partners (it owns the toggle and the "show inactive" checkbox --
    // filtering them out here would hide them from the one screen used to
    // turn them back on). Surfaces that should respect the toggle read
    // `activePartners` instead.
    const data = await fetchPartners({ includeInactive: true });
    setPartners(data);
    const savedId = localStorage.getItem('charlie_active_partner_id');
    // v1.7.40.89 -- real: never restore an INACTIVE partner as the active
    // persona. Toggling a partner off while it happens to be selected
    // would otherwise leave Shane sitting in a switched-off persona on
    // next load, with no obvious way to tell why things looked wrong.
    const selectable = data.filter((p) => p.active !== false);
    const restored = selectable.find((p) => p.id === WELL_REPD_PARTNER_ID) || selectable.find((p) => p.id === savedId) || selectable[0] || data[0];
    if (restored) setActivePartner(restored);
    return data;
  }, [setActivePartner]);

  /**
   * Load all persisted auth tokens from Supabase and index by partnerId.
   * Non-expired tokens are ready to use without re-auth.
   */
  const loadPersistedTokens = useCallback(async () => {
    const tokens = await loadAllTokens();
    const map = {};
    const now = Date.now();
    for (const t of tokens) {
      if (!map[t.partner_id]) map[t.partner_id] = {};
      const expiresAt = new Date(t.expires_at).getTime();
      const buffer = 5 * 60 * 1000;
      map[t.partner_id][t.provider] = {
        ...t,
        expired: now >= expiresAt - buffer,
      };
    }
    setPartnerTokens(map);
    return map;
  }, []);

  /**
   * Refresh token map for a single partner after connect/disconnect.
   */
  const refreshPartnerTokens = useCallback(async (partnerId) => {
    const tokens = await loadAllTokens();
    setPartnerTokens((prev) => {
      const map = { ...prev };
      map[partnerId] = {};
      const now = Date.now();
      for (const t of tokens.filter((t) => t.partner_id === partnerId)) {
        const expiresAt = new Date(t.expires_at).getTime();
        map[partnerId][t.provider] = {
          ...t,
          expired: now >= expiresAt - (5 * 60 * 1000),
        };
      }
      return map;
    });
  }, []);

  /**
   * Get a live access token for the active partner by provider.
   * Checks Supabase-restored tokens first, then falls back to localStorage (Google legacy).
   */
  const getPartnerToken = useCallback((partnerId, provider) => {
    const record = partnerTokens[partnerId]?.[provider];
    // Saved sign-ins no longer carry a token in the browser (status only). Use fetchAccessToken (supabaseAuth) when a live token is needed.
    // Google legacy fallback
    if (provider === 'google') {
      const legacy = getStoredGoogleToken(record?.email || '');
      if (legacy) return legacy.access_token;
    }
    return null;
  }, [partnerTokens]);

  const loadContacts = useCallback(async () => {
    // Main contact list excludes new_arrivals (lobby) and rejected (turned away).
    // Legacy rows with NULL workflow_status are treated as active.
    // Supabase caps a select at 1000 rows, so this goes through the one pager (src/lib/fetchAll.js, #2761): stable order (last_name, then id), the first four pages
    // together (four round trips used to be about 3.5 s on a cold load), a failed page throws. On a failure the list already loaded stays: a partial list looks complete.
    try {
      const all = await fetchAllRows(supabase, 'contacts', {
        select: '*, companies(name, company_type)',
        filter: (q) => withContactOwner(q, activePartner).or('workflow_status.is.null,workflow_status.eq.active').eq('archived', false).is('deleted_at', null),
        order: ['last_name'],
        parallelFirst: 4,
        label: 'Contacts',
      });
      setContacts(all);
    } catch (e) { console.error('[loadContacts]', e.message); }
  }, [activePartner]);

  const loadNewArrivals = useCallback(async () => {
    try {
      const data = await fetchAllRows(supabase, 'contacts', { select: '*, companies(name)', filter: (q) => withContactOwner(q, activePartner).eq('workflow_status', 'new_arrival'), order: [{ column: 'apollo_revealed_at', ascending: false, nullsFirst: false }], label: 'New arrivals' });
      setNewArrivals(data);
    } catch (e) { console.error('[loadNewArrivals]', e.message); }
  }, [activePartner]);

  const loadMergeCandidates = useCallback(async () => {
    const { data } = await supabase
      .from('merge_candidates')
      .select('*, contact_a:contact_a_id(id,first_name,last_name,email,job_title,linkedin_url,created_at), contact_b:contact_b_id(id,first_name,last_name,email,job_title,linkedin_url,created_at)')
      .eq('status', 'pending')
      .order('confidence', { ascending: false });
    setMergeCandidates(data || []);
  }, []);

  const loadCompanies = useCallback(async () => {
    // Same cap, same pager (#1538 first fixed this one by hand; #2761 puts every full-list read on one helper). Live companies only, name then id so ties never skip a row.
    try {
      const all = await fetchAllRows(supabase, 'companies', { select: '*', filter: (q) => q.is('deleted_at', null).is('merged_into_id', null), order: ['name'], label: 'Companies' });
      setCompanies(all);
    } catch (e) { console.error('[loadCompanies]', e.message); }
  }, []);

  const loadTasks = useCallback(async () => {
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .order('due_date');
    setTasks(data || []);
  }, []);

  // One template load at a time: at start the boot and the auth-state event (INITIAL_SESSION / SIGNED_IN) both ask for them, which read the same list twice in the same moment. A call
  // that arrives while one is running shares it; a later call (focus, a save) starts a fresh read as before.
  const templatesInflight = useRef(null);
  const loadTemplates = useCallback(() => {
    if (templatesInflight.current) return templatesInflight.current;
    const run = loadTemplatesNow().finally(() => { templatesInflight.current = null; });
    templatesInflight.current = run;
    return run;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplatesNow = useCallback(async () => {
    // v1.6.50 — hardened template loader.
    //   * Waits for a real Supabase session before hitting the API (no cold-boot race).
    //   * Up to 4 attempts w/ exponential backoff (300ms → 600ms → 1200ms → 2400ms).
    //   * NEVER overwrites a non-empty templates state with [] from the server.
    //     If the server returns 0 rows AND we already have cached rows, we KEEP
    //     the cache and warn — protects against RLS/session drift blanking the picker.
    //   * Persists last known good result to localStorage for next cold boot.
    const attempt = async () => {
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .order('name');
      if (error) return { ok: false, error };
      if (!Array.isArray(data)) return { ok: false, error: new Error('non-array response') };
      return { ok: true, data };
    };

    // Wait for the session to actually exist before the first attempt — fixes
    // the cold-boot race where the fetch fires before auth is ready.
    try { await supabase.auth.getSession(); } catch (_) {}

    const delays = [0, 300, 600, 1200, 2400];
    let result = null;
    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
      result = await attempt();
      if (result.ok) break;
      // eslint-disable-next-line no-console
      console.warn(`[charlie] loadTemplates attempt ${i + 1} failed:`, result.error?.message);
      // Refresh session between attempts in case the token expired mid-boot.
      try { await supabase.auth.getSession(); } catch (_) {}
    }

    if (!result || !result.ok) {
      // eslint-disable-next-line no-console
      console.warn('[charlie] loadTemplates: all retries exhausted, keeping cached state');
      return;
    }

    // Empty-response guard: if server returned [] but we already have templates
    // in state (either from cache or a prior successful load), do NOT clobber.
    setTemplates((prev) => {
      if (result.data.length === 0 && Array.isArray(prev) && prev.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[charlie] loadTemplates: server returned [] but cache has', prev.length, 'templates — keeping cache');
        return prev;
      }
      // Persist good result for next cold boot.
      if (result.data.length > 0) {
        try { localStorage.setItem('charlie_templates_cache_v1', JSON.stringify(result.data)); } catch (_) {}
      }
      return result.data;
    });
  }, []);

  // v1.6.50 — self-heal on tab focus + auth state change. If the user tabs
  // away and back, or the session refreshes, silently reconcile with the server.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadTemplates();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        loadTemplates();
      }
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      try { authSub?.subscription?.unsubscribe?.(); } catch (_) {}
    };
  }, [loadTemplates]);

  const loadSequences = useCallback(async () => {
    const { data } = await supabase
      .from('sequences')
      .select('*, sequence_steps(*)')
      .order('name');
    setSequences(data || []);
  }, []);

  const loadUsers = useCallback(async () => {
    const list = await fetchActiveUsers();
    setUsers(list);
    const def = resolveDefaultSender(list, null);
    if (def) setDefaultSenderId(def.id);
    return list;
  }, []);

  const startHeavyRef = useRef(null);
  const ensureAppData = useCallback(() => { if (startHeavyRef.current) startHeavyRef.current(); }, []);

  useEffect(() => {
    // v1.6.54: boot hardening balances "never hang" with "never leave data empty".
    // Previously v1.6.52 killed slow loaders at 6s and left arrays empty (contacts
    // showing 0). Now: fast unlock (UI never hangs) + background retry for any
    // loader that timed out, so contacts/companies backfill when Supabase recovers.
    async function boot() {
      const FAST_UNLOCK_MS = 10000;   // UI can render after this even if some loads pending
      const PER_LOADER_MS = 15000;    // give slow queries a real chance
      const RETRY_BACKOFF_MS = [2000, 5000, 10000, 20000]; // background retry cadence

      const wrap = (fn, label) => {
        const attempt = () => Promise.race([
          Promise.resolve().then(fn).then(() => ({ ok: true, label })).catch((e) => {
            console.warn(`[boot] ${label} threw:`, e?.message || e);
            return { ok: false, label, error: e };
          }),
          new Promise((resolve) => setTimeout(() => {
            console.warn(`[boot] ${label} timed out after ${PER_LOADER_MS / 1000}s`);
            resolve({ ok: false, label, timeout: true });
          }, PER_LOADER_MS)),
        ]);

        return attempt().then((res) => {
          if (!res.ok) {
            // Background retry loop — doesn't block boot, but ensures data eventually loads.
            let attemptNum = 0;
            const retry = () => {
              if (attemptNum >= RETRY_BACKOFF_MS.length) {
                console.warn(`[boot] ${label} gave up after ${RETRY_BACKOFF_MS.length} retries`);
                return;
              }
              const delay = RETRY_BACKOFF_MS[attemptNum++];
              setTimeout(async () => {
                console.log(`[boot] retrying ${label} (attempt ${attemptNum})`);
                try {
                  await fn();
                  console.log(`[boot] ${label} recovered on retry ${attemptNum}`);
                } catch (e) {
                  console.warn(`[boot] ${label} retry ${attemptNum} failed:`, e?.message || e);
                  retry();
                }
              }, delay);
            };
            retry();
          }
          return res;
        });
      };

      const fastUnlock = setTimeout(() => {
        console.warn('[boot] fast unlock hit — rendering UI; slow loaders will backfill');
        setLoading(false);
      }, FAST_UNLOCK_MS);

      try {
        await wrap(() => handleMsRedirect(), 'handleMsRedirect');
        try { setMsAccounts(getMsAccounts()); } catch (e) { console.warn('[boot] getMsAccounts threw:', e?.message); }
        // Opening straight on Lead Management: it reads its own lists and does not use the app-wide contacts, new arrivals or companies (four pages of whole contact
        // rows, the slowest thing here), so they are NOT started with it. wrap() runs its loader the moment it is called, so the three loads are thunks and only start when
        // wanted: right away anywhere else; from Lead Management, when the person leaves it (the other screens read them) or 45 s after as a safety net.
        const startsOnLeads = typeof window !== 'undefined' && /^#\/?(lead-management|lobby)(\/|\?|$)/.test(window.location.hash || '');
        const heavy = [
          () => wrap(() => loadContacts(), 'loadContacts'),
          () => wrap(() => loadNewArrivals(), 'loadNewArrivals'),
          () => wrap(() => loadCompanies(), 'loadCompanies'),
        ];
        let heavyStarted = false;
        let heavyTimer = null;
        const startHeavy = () => {
          if (heavyStarted) return [];
          heavyStarted = true;
          clearTimeout(heavyTimer);
          return heavy.map((f) => f());
        };
        startHeavyRef.current = startHeavy; // ensureAppData(): App calls it when the person leaves Lead Management
        if (startsOnLeads) heavyTimer = setTimeout(startHeavy, 45000); // only a safety net: going to any other page starts them at once (ensureAppData), and a slow Lead Management load must never trigger them
        await Promise.allSettled([
          wrap(() => loadPartners(), 'loadPartners'),
          ...(startsOnLeads ? [] : startHeavy()),
          wrap(() => loadTasks(), 'loadTasks'),
          wrap(() => loadTemplates(), 'loadTemplates'),
          wrap(() => loadSequences(), 'loadSequences'),
          wrap(() => loadUsers(), 'loadUsers'),
          wrap(() => loadPersistedTokens(), 'loadPersistedTokens'),
        ]);
      } catch (err) {
        console.error('[boot] outer error:', err);
      } finally {
        clearTimeout(fastUnlock);
        setLoading(false);
      }
    }
    boot();
  }, []); // eslint-disable-line

  const value = {
    partners, setPartners, loadPartners,
    // v1.7.40.89 -- real, the toggle-respecting list. `partners` is the
    // complete set (admin surfaces need it); `activePartners` is what the
    // sidebar switcher, pickers, and rollups should use.
    // #1814 (2026-08-24) -- real, Stinger direct: excludes child
    // partners folded into their parent's own dashboard as a module.
    // CORRECTED same day, also Stinger direct: this originally derived
    // "folded into parent" from parent_partner_id alone (any partner
    // with a parent got excluded). That was wrong as a general rule --
    // real corporate structure and how a partner is actually WORKED
    // are two different things. FoxSet is a real subsidiary of Silver
    // Fox Completions on paper, but is worked as its own independent
    // persona, not first through Silver Fox -- excluding it from the
    // switcher was a real regression. Now driven by the explicit
    // hide_from_switcher flag instead, set true only for partners
    // Stinger has actually confirmed should fold into their parent's
    // page (He Wins Athletes) -- parent_partner_id itself is untouched,
    // still real and useful elsewhere, just no longer the trigger here.
    activePartners: partners.filter((p) => p.active !== false && !p.hide_from_switcher),
    activePartner, setActivePartner, setSessionPartner, chooseActivePartner, personaSwitchSignal,
    viewMode, setViewMode: setViewModeAndPersist,
    viewAsUserId, setViewAsUserId,
    ensureAppData,
    contacts, setContacts, loadContacts,
    newArrivals, setNewArrivals, loadNewArrivals,
    mergeCandidates, setMergeCandidates, loadMergeCandidates,
    companies, setCompanies, loadCompanies,
    tasks, setTasks, loadTasks,
    templates, setTemplates, loadTemplates,
    sequences, setSequences, loadSequences,
    users, setUsers, loadUsers,
    defaultSenderId, setDefaultSenderId,
    msAccounts, setMsAccounts,
    getStoredGoogleToken,
    // Persistent auth
    partnerTokens,
    getPartnerToken,
    refreshPartnerTokens,
    getGoogleTokenForPartner,
    loading, toast, showToast, dismissToast,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}
